import { useFleetioWorkOrdersQuery } from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import RedAlert from '@/gf/components/RedAlert'
import Spinner from '@/gf/components/Spinner'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@/gf/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/gf/components/ui/popover'
import SelectorTrigger from '@/gf/components/ui/selector-trigger'
import useDebounce from '@/gf/hooks/useDebounce'
import { useMemo, useState } from 'react'

const FleetioWorkOrderNumber = ({
  workOrderId,
  orgMachineId,
  onChange,
}: {
  workOrderId: number | undefined
  orgMachineId: string
  onChange: (workOrderId: number) => void
}) => {
  const client = useGqlClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const { data, loading, error } = useFleetioWorkOrdersQuery({
    client,
    variables: { orgMachineId },
  })

  const workOrders = useMemo(() => {
    return data?.fleetioWorkOrders ?? []
  }, [data])

  // Show the human-readable WO Number, not Fleetio's internal id — mechanics
  // recognise the number, not the integer key. The id stays the unit of
  // exchange with the parent (and the BE resolves the number from Fleetio
  // server-side, so onChange's number-less signature is fine).
  const selectedNumber = workOrders.find((wo) => wo.id == workOrderId)?.number

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SelectorTrigger value={selectedNumber} placeholder="Select work order" />
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Search work order..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {error ? (
              <RedAlert className="bg-transparent" title="Error loading work orders." />
            ) : loading ? (
              <CommandLoading>
                <div className="flex flex-row gap-x-2 items-center justify-center">
                  <Spinner className="mx-0" size="xs" color="blue" />
                  Searching work orders...
                </div>
              </CommandLoading>
            ) : !debouncedSearch ? (
              <CommandEmpty>Search for work orders.</CommandEmpty>
            ) : workOrders.length === 0 ? (
              <CommandEmpty>No work orders found.</CommandEmpty>
            ) : null}
            <CommandGroup>
              {workOrders.map((workOrder) => (
                <CommandItem
                  key={workOrder.id}
                  value={workOrder.number}
                  keywords={[`${workOrder.id}`, workOrder.number, workOrder.state]}
                  onSelect={() => {
                    setSearch('')
                    setOpen(false)
                    onChange(workOrder.id)
                  }}
                >
                  <div className="flex flex-col gap-y-0 items-start">
                    <span className="font-medium">{workOrder.number}</span>
                    <span className="text-gray-700 text-sm">
                      ID: {workOrder.id}, Number: {workOrder.number}, State: {workOrder.state},
                      Status: {workOrder.status}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default FleetioWorkOrderNumber
